import { useCallback, useEffect, useState } from "react";
import type { WaitingListPopup } from "./WaitingListPopup";

/**
 * Transport for the waiting-list popup, injected by each surface because they
 * use different HTTP clients + base URLs (the widget: two axios instances; the
 * webcomponents: fetch + apiUrl). Given a method + a RELATIVE path (+ body for
 * POST), it must resolve/return the parsed JSON payload, and throw on a POST
 * failure (so `onSubmit` can surface the server message).
 */
export type WaitingListRequest = (
  method: "GET" | "POST",
  path: string,
  body?: unknown
) => Promise<any>;

export interface WaitingListPopupData {
  course?: any;
  session?: any;
  location?: any;
  canJoinAnyDate?: boolean;
  alternativeDates?: WaitingListPopup.AlternativeDate[];
  similarExperiences?: WaitingListPopup.SimilarExperience[];
  term?: WaitingListPopup.Term | null;
  hasDropInEnabled?: boolean;
  eligible?: boolean;
  bookableNow?: boolean;
  availableDates?: WaitingListPopup.AlternativeDate[];
}

const DEFAULT_DATA: WaitingListPopupData = {
  course: undefined,
  session: undefined,
  location: undefined,
  canJoinAnyDate: false,
  alternativeDates: [],
  similarExperiences: [],
  term: undefined,
  hasDropInEnabled: false,
  eligible: false,
  bookableNow: false,
  availableDates: []
};

/**
 * Shared data + submit logic for the waiting-list popup, used by the widget
 * (JoinWaitingListPopup) and the webcomponents (WaitingListPortal). Fetches the
 * popup payload and routes the join POST to the right list (term / session /
 * course). Rendering, cross-sell navigation, and (in the webcomponents) the
 * portal + shadow-DOM shell + success events stay in each wrapper.
 *
 * Relative paths (the caller's `request` prepends the base):
 *   GET  popups/waiting-list/term/:eventId
 *        popups/waiting-list/:listingId[/:sessionId]
 *   POST term/:eventId/waiting-list
 *        session/:sessionId/waiting-list
 *        course/:listingId/waiting-list
 *
 * NOTE: memoize `request` in the caller (e.g. useCallback) so the fetch effect
 * doesn't re-run every render.
 */
export function useWaitingListPopup({
  listingId,
  sessionId,
  eventId,
  request,
  enabled = true
}: {
  listingId?: string;
  sessionId?: string;
  eventId?: string;
  request: WaitingListRequest;
  enabled?: boolean;
}): {
  data: WaitingListPopupData;
  isLoading: boolean;
  onSubmit: (params: WaitingListPopup.SubmitParams) => Promise<any>;
} {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<WaitingListPopupData>(DEFAULT_DATA);

  useEffect(() => {
    if (!enabled || !listingId) return;
    let cancelled = false;
    setIsLoading(true);
    setData(DEFAULT_DATA);
    (async () => {
      try {
        const path = eventId
          ? `popups/waiting-list/term/${eventId}`
          : `popups/waiting-list/${listingId}${sessionId ? `/${sessionId}` : ""}`;
        const payload = await request("GET", path);
        if (!cancelled && payload) {
          setData(prev => ({ ...prev, ...payload }));
        }
      } catch {
        // Best-effort: the popup still renders the join form from `course`.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, listingId, sessionId, eventId, request]);

  const onSubmit = useCallback(
    async (params: WaitingListPopup.SubmitParams) => {
      const termEventId = data.term?.eventId;
      let path: string;
      if (termEventId) {
        if (params.target === "session" && params.sessionId) {
          path = `session/${params.sessionId}/waiting-list`;
        } else if (params.target === "course") {
          path = `course/${listingId}/waiting-list`;
        } else {
          path = `term/${termEventId}/waiting-list`;
        }
      } else {
        const joinCourseWide = params.target === "course" || !data.session;
        path = joinCourseWide
          ? `course/${listingId}/waiting-list`
          : `session/${data.session._id}/waiting-list`;
      }
      return request("POST", path, { params });
    },
    [data, listingId, request]
  );

  return { data, isLoading, onSubmit };
}
